<template>
    <div class="card mt-2">
        <div class="card-header d-flex justify-content-between align-items-start">
            <h5 class="card-title mt-1">Assigned Staff</h5>

            <div>
                <b-button variant="outline-secondary bg-light mr-1"
                          size="sm"
                          :aria-expanded="assignmentsListExpanded ? 'true' : 'false'"
                          aria-controls="assignmentsListCollapse"
                          @click="assignmentsListExpanded = !assignmentsListExpanded">
                    {{ (assignmentsListExpanded ? 'Collapse' : 'Expand') + ' (' + staffWorkItemAssignments.length + ')' }}
                </b-button>
                <button type="button"
                        class="btn btn-sm btn-outline-secondary mr-2"
                        v-on:click="openAssignmentScreen"
                        v-if="!readonly && ((assignmentOption == 'DelayedAssignmentOnly') || delayedAssignedAudioLengthMode || (assignmentOption == 'LiveAssignmentOnly') || liveRoundRobinMode)">
                    <i class="fas fa-plus mr-1"></i>Edit
                </button>
                <b-dropdown size="sm"
                            variant="outline-secondary"
                            class="mr-2"
                            split
                            right
                            v-on:click="openAssignmentScreen"
                            v-if="!readonly && (assignmentOption != 'DelayedAssignmentOnly') && !delayedAssignedAudioLengthMode && (assignmentOption != 'LiveAssignmentOnly') && !liveRoundRobinMode">
                    <template v-slot:button-content>
                        <i class="fas fa-plus mr-1"></i>Edit
                    </template>

                    <b-dropdown-item v-on:click="openAssignmentScreen('delayed')">Delayed Job Assignment</b-dropdown-item>
                    <b-dropdown-item v-on:click="openAssignmentScreen('live')">Live Job Assignment</b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <b-collapse id="assignmentsListCollapse" class="w-100 m-0 p-0" v-model="assignmentsListExpanded">

            <b-table-simple hover small caption-top responsive>

                <b-thead head-variant="secondary">
                    <b-tr>
                        <b-th class="pl-3">Name of staff</b-th>
                        <b-th>Roles</b-th>
                        <b-th class="text-center">Notified at</b-th>
                        <b-th class="text-center">Expires at</b-th>
                        <b-th class="text-center">Reacted at</b-th>
                        <b-th class="text-center" v-if="!liveRoundRobinMode">Minutes</b-th>
                        <b-th class="text-center" v-if="liveRoundRobinMode">Turn Sequence</b-th>
                        <b-th class="text-center">Status</b-th>
                        <b-th class="text-right pr-3">Comment</b-th>
                    </b-tr>
                </b-thead>

                <b-tbody>

                    <b-tr v-if="staffWorkItemAssignments.length == 0">
                        <b-td colspan="8">Currently no staff assigned</b-td>
                    </b-tr>

                    <b-tr v-else v-for="(staffWorkItemAssignment) in staffWorkItemAssignments" v-bind:key="staffWorkItemAssignment.staffWorkItemAssignmentId">
                        <b-td class="pl-3">{{ staffWorkItemAssignment.userAccountDisplayName }}</b-td>
                        <b-td>{{ staffWorkItemAssignment.workItemRelatedRoles.displayName }}</b-td>
                        <b-td class="text-center">{{ formatDateTime(staffWorkItemAssignment.createdDateTimeUTC, 'DD/MM/YYYY hh:mmA', true, false) }}</b-td>

                        <b-td class="text-center">{{ formatDateTime(staffWorkItemAssignment.expiryDateTimeUTC, 'DD/MM/YYYY hh:mmA', true, false) }}</b-td>



                        <b-td class="text-center">{{ staffWorkItemAssignment.finalReactionDateTimeUTC ? formatDateTime(staffWorkItemAssignment.finalReactionDateTimeUTC, 'DD/MM/YYYY hh:mmA', true, false) : formatDateTime(staffWorkItemAssignment.initialReactionDateTimeUTC, 'DD/MM/YYYY hh:mmA', true, false) }}</b-td>
                        <b-td class="text-center" v-if="!liveRoundRobinMode">{{ staffWorkItemAssignment.audioMinutes }}</b-td>
                        <b-td class="text-center" v-if="liveRoundRobinMode">{{ staffWorkItemAssignment.roundRobinPriority }}</b-td>
                        <b-td class="text-center">{{ staffWorkItemAssignment.status.displayName }}</b-td>
                        <b-td class="text-right pr-3">

                            <b-badge v-if="staffWorkItemAssignment.reactionComment" variant="info" class="p-2" :id="'comment-popover-' + staffWorkItemAssignment.staffWorkItemAssignmentId">
                                Comment
                                <b-popover :target="'comment-popover-' + staffWorkItemAssignment.staffWorkItemAssignmentId" triggers="hover" placement="left">
                                    <template #title>
                                        {{ staffWorkItemAssignment.userAccountDisplayName }} commented:
                                    </template>
                                    {{ staffWorkItemAssignment.reactionComment }}
                                </b-popover>
                            </b-badge>
                        </b-td>
                    </b-tr>
                </b-tbody>
                <b-tfoot>
                    <b-tr>
                        <b-td colspan="8" variant="secondary" class="text-left pl-3">
                            <span class="font-weight-bold">Total assigned staff: </span>{{ staffWorkItemAssignments.length }}
                        </b-td>
                    </b-tr>
                </b-tfoot>

            </b-table-simple>

        </b-collapse>

        
        <availability-delayed-assignment :work-item-id="workItemId"
                                         :current-user-account-id="currentUserAccountId"
                                         :start-date="delayedAssignmentStartDate"
                                         :due-date="delayedAssignmentDueDate"
                                         :api-controller-url="apiUrl + '/staffavailability/'"
                                         :api-assignment-controller-url="apiUrl + '/staffworkitemassignment/'"
                                         :images-url="imagesUrl"
                                         @change="reloadAssignedStaff()"
                                         :assignment-mode="true"
                                         :relevant-roles="relevantRoles"
                                         :roles-with-audio-minutes="rolesWithAudioMinutes"
                                         :roles-with-round-robin-priority="rolesWithRoundRobinPriority"
                                         :checked-roles="null"
                                         title="Delayed job assignment"
                                         v-model="delayedAudioMinutesEditDialogOpen"
                                         :court-registry-id="courtRegistryId"
                                         :interview-organisation-id="interviewOrganisationId"
                                         :time-zone-id="timeZoneId"
                                         :default-response-time="defaultLiveAssignmentResponseTime"
                                         :all-default-response-time-options="allDefaultResponseTimeOptions"
                                         v-if="(assignmentOption == 'DelayedAssignmentOnly') || (assignmentOption == 'DelayedAssignmentPreferred') || (assignmentOption == 'LiveAssignmentPreferred') "
                                         @modalOpen="emitModalStatus"
                                         :turn-allocation-mode="turnAllocationMode">
        </availability-delayed-assignment>

        <availability-day-accumulated :work-item-id="workItemId"
                                      :current-user-account-id="currentUserAccountId"
                                      :api-controller-url="apiUrl + '/staffavailability/'"
                                      :api-assignment-controller-url="apiUrl + '/staffworkitemassignment/'"
                                      :images-url="imagesUrl"
                                      @change="reloadAssignedStaff()"
                                      :assignment-mode="true"
                                      :relevant-date="liveAssignmentDate"
                                      :relevant-start-time="liveAssignmentStartTime"
                                      :relevant-end-time="liveAssignmentEndTime"
                                      :relevant-roles="relevantRoles"
                                      :roles-with-audio-minutes="rolesWithAudioMinutes"
                                      :roles-with-round-robin-priority="rolesWithRoundRobinPriority"
                                      :checked-roles="null"
                                      title="Live job assignment"
                                      v-model="runningAssignmentEditDialogOpen"
                                      :court-registry-id="courtRegistryId"
                                      :interview-organisation-id="interviewOrganisationId"
                                      :time-zone-id="timeZoneId"
                                      :default-response-time="defaultDelayedAssignmentResponseTime"
                                      :all-default-response-time-options="allDefaultResponseTimeOptions"
                                      :round-robin-mode="liveRoundRobinMode"
                                      v-if="(assignmentOption == 'LiveAssignmentOnly') || (assignmentOption == 'LiveAssignmentPreferred') || (assignmentOption == 'DelayedAssignmentPreferred')"
                                      @modalOpen="emitModalStatus"
                                      :turn-allocation-mode="turnAllocationMode">
        </availability-day-accumulated>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';

    import ErrorsMixin from '../mixins/ErrorsMixin';
    import DateTimeMixin from '../mixins/DateTimeMixin';

    import AvailabilityStaffList from './Availability/AvailabilityStaffList';
    import AvailabilityDelayedAssignment from './Availability/AvailabilityDelayedAssignment';
    import AvailabilityDayAccumulated from './Availability/AvailabilityDayAccumulated';

    Vue.use(BootstrapVue);
    Vue.component('availability-staff-list', AvailabilityStaffList);
    Vue.component('availability-delayed-assignment', AvailabilityDelayedAssignment);
    Vue.component('availability-day-accumulated', AvailabilityDayAccumulated);

    export default {
        name: 'StaffWorkitemAssignments',
        mixins: [ErrorsMixin, DateTimeMixin],
        model: {
            event: 'change',
            prop: 'assignedStaff'
        },
        props: {
            apiUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            assignmentOption: {
                type: String,
                required: true
            },
            // the 3 filter props
            courtRegistryId: {
                type: String,
                required: false,
                default: null
            },
            interviewOrganisationId: {
                type: String,
                required: false,
                default: null
            },
            timeZoneId: {
                type: String,
                required: false
            },

            // live assignment props
            liveAssignmentDate: {   
                type: Date,
                required: false,
                default: null
            },
            liveAssignmentStartTime: {
                type: Date,
                required: false,
                default: null
            },
            liveAssignmentEndTime: {
                type: Date,
                required: false,
                default: null
            },

            // delayed assignment props
            delayedAssignmentStartDate: { // never null
                type: Date,
                required: false,
                default: null
            },
            // highlight once in calendar view
            delayedAssignmentDueDate: { // never null
                type: Date,
                required: false,
                default: null
            },
            imagesUrl: {
                type: String,
                required: true
            },
            workItemId: {
                type: String,
                required: true
            },
            relevantRoles: {
                type: Array,
                required: true
            },
            rolesWithAudioMinutes: {
                type: Array,
                required: true
            },
            rolesWithRoundRobinPriority: {
                type: Array,
                required: true
            },
            readonly: {
                type: Boolean,
                default: false
            },
            defaultLiveAssignmentResponseTime: {
                type: Number
            },
            defaultDelayedAssignmentResponseTime: {
                type: Number
            },
            liveRoundRobinMode: {
                type: Boolean,
                default: false
            },
            delayedAssignedAudioLengthMode: {
                type: Boolean,
                default: false
            },
            allDefaultResponseTimeOptions: {
                type: Array,
                default: null
            },
            assignedStaff: {
                type: Array,
                default: null
            },
            turnAllocationMode: {
                type: Number,
                default: null
            }
        },
        data: function () {
            return {
                staffWorkItemAssignments: [],
                currentErrors: [],

                assignmentsListExpanded: true,

                // v-model for the existing assignment
                staffWorkItemAssignmentEditDialogOpen: false,

                // josefs 2 x new assignment v-model
                delayedAudioMinutesEditDialogOpen: false,
                runningAssignmentEditDialogOpen: false,

                currentAssignedStaff: this.assignedStaff ? this.assignedStaff : []
            };
        },
        computed: {
            showErrors: function () {
                return this.currentErrors.length > 0;
            }
        },
        methods: {

            emitModalStatus: function (modalStatus) {
                this.$emit('modalOpen', modalStatus);
            },

            reload: function () {

                var url = this.apiUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'staffworkitemassignment/byworkitem/';
                url += this.workItemId;

                if (this.workItemId) {
                    return axios
                        .get(url)
                        .then(response => {
                            this.staffWorkItemAssignments = response.data;
                            var newAssignedStaff = this.staffWorkItemAssignments.map(e => {
                                var currentAssignedStaff = {
                                    userAccountId: e.userAccountId,
                                    userAccountDisplayName: e.userAccountDisplayName,
                                    audioMinutes: e.audioMinutes,
                                    roundRobinPriority: e.roundRobinPriority,
                                    workItemRelatedRoles: e.workItemRelatedRoles,
                                };
                                if (!e.status || (
                                    (e.status.enumValue == this.$enum.StaffWorkItemAssignmentStatus.Disabled) ||
                                    (e.status.enumValue == this.$enum.StaffWorkItemAssignmentStatus.Rejected) ||
                                    (e.status.enumValue == this.$enum.StaffWorkItemAssignmentStatus.NoResponse))) {
                                    return null;
                                }
                                currentAssignedStaff.status = e.status;
                                return currentAssignedStaff;
                            });
                            newAssignedStaff = newAssignedStaff.filter(e => e != null);
                            newAssignedStaff.sort((a, b) => {
                                if (a.userAccountDisplayName < b.userAccountDisplayName) return -1;
                                if (a.userAccountDisplayName > b.userAccountDisplayName) return 1;
                                return 0;
                            });
                            var assignedStaffChanges = (newAssignedStaff.length != this.currentAssignedStaff.length);
                            if (!assignedStaffChanges) {
                                for (var i = 0; i < newAssignedStaff.length.length; i++) {
                                    if (newAssignedStaff[i].userAccountId != this.currentAssignedStaff[i].userAccountId) assignedStaffChanges = true;
                                    if (newAssignedStaff[i].status) {
                                        if (newAssignedStaff[i].status.stringValue != this.currentAssignedStaff[i].status.stringValue) assignedStaffChanges = true;
                                        if (newAssignedStaff[i].audioMinutes != this.currentAssignedStaff[i].audioMinutes) assignedStaffChanges = true;
                                        if (newAssignedStaff[i].roundRobinPriority != this.currentAssignedStaff[i].roundRobinPriority) assignedStaffChanges = true;
                                    }
                                    if (assignedStaffChanges) break;
                                }
                            }
                            if (assignedStaffChanges) {
                                this.currentAssignedStaff = newAssignedStaff;
                                this.$emit('change', this.currentAssignedStaff);
                            }
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
                return Promise.resolve();
            },

            reloadAssignedStaff: function () {
               this.reload();
            },

            openAssignmentScreen: function (assignmentType) {
                if (assignmentType == 'live') {
                    this.runningAssignmentEditDialogOpen = true;
                }
                else if (assignmentType == 'delayed') {
                    this.delayedAudioMinutesEditDialogOpen = true;
                }
                else if ((this.assignmentOption == 'LiveAssignmentOnly') || (this.assignmentOption == 'LiveAssignmentPreferred') || this.liveRoundRobinMode) {
                    this.runningAssignmentEditDialogOpen = true;
                }
                else if ((this.assignmentOption == 'DelayedAssignmentOnly') || (this.assignmentOption == 'DelayedAssignmentPreferred') || this.delayedAssignedAudioLengthMode) {
                    this.delayedAudioMinutesEditDialogOpen = true;
                }
            }
        },
        mounted: function () {
            this.reload();
        }  
    }
</script>

<style scoped>

    @media (min-width: 992px) {
        div >>> .modal .modal-huge {
            width: 90% !important;
            max-width: 1600px !important;
        }
    } 

</style>